import React from 'react';
import { Box, Typography, Stack, Chip, Divider } from '@mui/material';
import { AppointmentTypeChanges } from '../../types/appointmentChanges';
import { ExpandableFieldItem } from '@features/appointment-types';
import { formatAppointmentDuration } from '../../../../utils/durationUtils';
import { UserTemplateItem } from '../../../../types/types';

interface CreateNewPreviewContentProps {
  appointmentTypeName: string;
  changes?: AppointmentTypeChanges;
  currentDuration?: number;
  currentTelehealth?: boolean;
  currentForms?: ExpandableFieldItem[];
  currentReminders?: ExpandableFieldItem[];
  currentTemplate?: UserTemplateItem | null;
}

export const CreateNewPreviewContent: React.FC<CreateNewPreviewContentProps> = ({
  appointmentTypeName,
  changes,
  currentDuration,
  currentTelehealth,
  currentForms = [],
  currentReminders = [],
  currentTemplate,
}) => {
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Chip label="NEW" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {appointmentTypeName} (Copy)
        </Typography>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        A new appointment type will be created with your current settings.
      </Typography>

      <Divider sx={{ mt: 1.5, mb: 1 }} />

      {/* Show current settings */}
      <Stack spacing={1}>
        {/* Template */}
        {currentTemplate && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.25, display: 'block' }}
            >
              Template
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {currentTemplate.display_name || 'Unnamed Template'}
            </Typography>
          </Box>
        )}

        {/* Duration */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
            Duration
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {currentDuration || 30} min
          </Typography>
        </Box>

        {/* Telehealth */}
        {currentTelehealth !== undefined && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.25, display: 'block' }}
            >
              Type
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {currentTelehealth ? 'Telehealth' : 'In-person'}
            </Typography>
          </Box>
        )}

        {/* Reminders */}
        {currentReminders.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.25, display: 'block' }}
            >
              Reminders
            </Typography>
            <Stack spacing={0.25}>
              {currentReminders.map((reminder, idx) => (
                <Typography key={idx} variant="caption" sx={{ fontWeight: 500 }}>
                  Appointment Reminder • {formatAppointmentDuration(reminder.offset || '')}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}

        {/* Forms */}
        {currentForms.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.25, display: 'block' }}
            >
              Forms
            </Typography>
            <Stack spacing={0.25}>
              {currentForms.map((form, idx) => (
                <Typography key={idx} variant="caption" sx={{ fontWeight: 500 }}>
                  {form.name} • {formatAppointmentDuration(form.offset || '')}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
